import { useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FeeTableCard } from "@/components/admin/FeeTableCard";
import { EditableText } from "@/components/admin/EditableCell";
import { TypeBlock } from "@/components/admin/ExtrasEditor";
import { FishingEditor } from "@/components/admin/FishingEditor";

const BasisPicker = ({ g, set }) => (
  <div className="space-y-1.5">
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Beregnes etter</div>
    <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-white" role="radiogroup" aria-label="Beregnes etter">
      {[["length", "Lengdemeter"], ["bt", "Brutto tonn (BT)"]].map(([k, l]) => (
        <button key={k} type="button" role="radio" aria-checked={g.basis === k} onClick={() => set({ ...g, basis: k })} data-testid={`other-${g.id}-basis-${k}`}
          className={`px-3 py-1.5 text-sm rounded transition-colors ${g.basis === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
      ))}
    </div>
    <p className="text-xs text-slate-500">{g.basis === "length" ? "Brukeren skriver inn lengde i meter og velger periode, slik som for fritidsfartøy." : "Brukeren skriver inn brutto tonnasje og antall døgn, med avgiftstabellene under."}</p>
  </div>
);

const TypeChips = ({ g, set }) => {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (g.types.some((t) => t.toLowerCase() === v.toLowerCase())) { toast.error("Typen finnes allerede"); return; }
    set({ ...g, types: [...g.types, v.slice(0, 60)] });
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {g.types.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 pl-3 pr-1 py-1 text-sm text-slate-800" data-testid={`other-${g.id}-type-${i}`}>
            {t}
            <button type="button" onClick={() => set({ ...g, types: g.types.filter((_, j) => j !== i) })} aria-label={`Slett ${t}`}
              className="p-0.5 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`other-${g.id}-type-${i}-remove`}><X className="w-3.5 h-3.5" /></button>
          </span>
        ))}
        {!g.types.length && <span className="text-xs text-slate-500">Ingen typer – bolken vises ikke i kalkulatoren.</span>}
      </div>
      <div className="flex gap-2 max-w-sm">
        <input value={draft} maxLength={60} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Ny fartøystype" className="flex-1 h-9 px-3 rounded-md border border-slate-300 text-sm outline-none focus:border-sky-500" data-testid={`other-${g.id}-new-type`} />
        <Button size="sm" variant="outline" onClick={add} data-testid={`other-${g.id}-add-type`}><Plus className="w-4 h-4 mr-1" />Legg til</Button>
      </div>
    </div>
  );
};

const GroupCard = ({ g, set, newTable }) => {
  const setTables = (tables) => set({ ...g, tables });
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 space-y-4" data-testid={`other-group-${g.id}`}>
      <EditableText value={g.name} onCommit={(v) => set({ ...g, name: v })} testId={`other-${g.id}-name`} className="font-display text-base font-semibold text-slate-900" />
      <TypeChips g={g} set={set} />
      {g.lengths && <BasisPicker g={g} set={set} />}
      {g.lengths && g.basis === "length" ? (<>
        <FishingEditor fishing={g.lengths} onChange={(lengths) => set({ ...g, lengths })} prefix={`other-${g.id}-len`} />
        <TypeBlock t={`other-${g.id}-len`} label={`Avfallsgebyr – ${g.name}`} ex={g.lextras} set={(lextras) => set({ ...g, lextras })} />
      </>) : (<>
        {g.tables.map((t, i) => (
          <FeeTableCard key={t.id} table={t} onChange={(nt) => setTables(g.tables.map((x, j) => (j === i ? nt : x)))} canDelete={g.tables.length > 1}
            onDelete={() => setTables(g.tables.filter((_, j) => j !== i))} />
        ))}
        <Button size="sm" variant="outline" onClick={() => setTables([...g.tables, newTable()])} data-testid={`other-${g.id}-add-table`}><Plus className="w-4 h-4 mr-1" />Ny tabell</Button>
        <TypeBlock t={`other-${g.id}`} label={`ISPS og avfallsgebyr – ${g.name}`} ex={g.extras} set={(extras) => set({ ...g, extras })} />
      </>)}
    </div>
  );
};

export const OtherTypesEditor = ({ c, save, newTable }) => {
  const setGroup = (i, g) => save("other_groups", c.other_groups.map((x, j) => (j === i ? g : x)));
  return (
    <div className="space-y-4" data-testid="other-section">
      <div>
        <h3 className="font-display text-lg font-semibold text-slate-900">Andre fartøystyper</h3>
        <p className="text-xs text-slate-500">Vises nederst i nedtrekksmenyen. Brukeren velger deretter type fartøy. Hver bolk har egne priser.</p>
      </div>
      {c.other_groups.map((g, i) => <GroupCard key={g.id} g={g} set={(ng) => setGroup(i, ng)} newTable={newTable} />)}
    </div>
  );
};
